import { useMemo } from 'react';

import { useFormListValidator } from 'core/Hooks';
import { toTitleCase } from 'core/Utils';
import {
  AdditionalFieldsFormsGuard,
  CurrencyInput,
  Group,
  ImageUpload,
  Input,
  LoadingOverlay,
  NumberInput,
  Select,
  Stack,
  TextInput,
} from 'ui';
import { hasLength, isEmail, isNotEmpty, useForm } from 'ui/Form';

import { useCrowdfundingProvider, useManagementTranslations } from '../../../../../../Context';
import {
  PeerTeam,
  PeerTypes,
  StoreItemImage,
  useShowCampaignById_new,
  useShowCurrentCampaign,
} from '../../../../../../api';
import {
  useListCampaignPeers,
  useShowCampaignPeer,
} from '../../../../../../api/new/campaigns/peers/get';
import {
  PeerParticipantsWith__Type,
  PeerTeamWith__Type,
} from '../../../../../../api/new/campaigns/peers/types';
import {
  useCampaignInfoByProvider,
  useShowCampaignSettings,
} from '../../../../../../api/new/campaigns/utils/campaignUtils';

export type TeamFormType = {
  name: string;
  email?: string;
  goal?: string;
  image?: StoreItemImage;
  file?: File | null;
};
export type ParticipantFormType = {
  first_name: string;
  last_name: string;
  email?: string;
  team_id?: PeerTeam['uuid'];
  options?: Record<string, string | string[]>;
  goal?: string;
  image?: StoreItemImage;
  file?: File | null;
};
const AddEditTeamForm = ({ teamData }: { teamData?: PeerTeamWith__Type }) => {
  const { causeId, campaignId } = useCampaignInfoByProvider();

  const { data: campaign } = useShowCampaignById_new({ campaignId, causeId });
  const { t } = useManagementTranslations();

  const requestEmail = campaign?.team_options.registration_options.email.requested;
  const requestGoal = campaign?.team_options.registration_options.goal.requested;

  const emailRequired = campaign?.team_options.registration_options.email.required;
  const goalRequired = campaign?.team_options.registration_options.goal.required;

  const form = useForm<TeamFormType>({
    initialValues: {
      name: teamData?.name ?? '',
      email: teamData?.email ?? '',
      goal: teamData?.goal ?? '',
      file: null,
      image: teamData?.image ?? undefined,
    },
    validate: {
      name: isNotEmpty('Field cannot be empty'),
      email: emailRequired ? isEmail('Invalid email address') : undefined,
      goal: goalRequired ? isNotEmpty('Field cannot be empty') : undefined,
    },
  });

  useFormListValidator({ form, id: 'team' });

  return (
    <Stack gap="lg" key={teamData?.uuid} mb="lg">
      {/* one way we can overcome weird spacing with errors is to add align='flex-start' to Groups */}
      <Group grow align="flex-start">
        <TextInput
          label={`${t('team')} Name`}
          h="100%"
          placeholder={`Enter ${t('team').toLowerCase()} name`}
          required
          {...form.getInputProps('name')}
        />
        {requestEmail && (
          <TextInput
            label={`${t('team')} Email Address`}
            h="100%"
            placeholder={`Enter ${t('team').toLowerCase()} email address`}
            required={emailRequired}
            {...form.getInputProps('email')}
          />
        )}
      </Group>
      {requestGoal && (
        <CurrencyInput
          allowDecimal={false}
          placeholder="$ 0"
          label={`${t('team')} Fundraising Goal`}
          h="100%"
          maw="calc(50% - 12px)"
          required={goalRequired}
          {...form.getInputProps('goal')}
        />
      )}
      <Stack gap={4}>
        <ImageUpload
          label={`Upload a ${t('team')} Image`}
          dimensions={[600, 300]}
          file={form.values.file}
          setFile={(file) => {
            form.setFieldValue('file', file);
          }}
          existingSrc={form.values.image}
          setExistingSrc={() => form.setFieldValue('image', undefined)}
          enableCropping
        />
        {form.errors.file && <Input.Error>{form.errors.file}</Input.Error>}
      </Stack>
    </Stack>
  );
};

const AddEditParticipantForm = ({
  participantData,
}: {
  participantData?: PeerParticipantsWith__Type;
}) => {
  const { data: campaign } = useShowCurrentCampaign();

  const isAthon = campaign?.__type === 'athon';

  const unit = isAthon && toTitleCase(campaign?.units_of_activity_singular ?? '');

  const { data: teamsData } = useListCampaignPeers({
    causeId: campaign?.cause_id,
    campaignId: campaign?.uuid,
    peer_type: 'teams',
    select: (data) => data?.teams,
  });

  const { t } = useManagementTranslations();
  const { data: campaignSettings } = useShowCampaignSettings();

  const teams = teamsData?.map((team) => {
    return { label: team.name, value: team.uuid };
  });
  const registrationFields = campaign?.participant_options.registration_options;

  const registrationValues =
    registrationFields?.map((field) => ({
      name: field.name,
      required: field.required,
    })) ?? [];

  const registrationOptions = registrationValues.reduce((accumulator, value) => {
    return { ...accumulator, [value.name]: '' };
  }, {});

  const isSetGoal = campaign?.participant_options.goal_setting === 'set_goal';

  const form = useForm<ParticipantFormType>({
    initialValues: {
      first_name: participantData?.first_name ?? '',
      last_name: participantData?.last_name ?? '',
      email: participantData?.email ?? '',
      team_id: participantData?.team?.uuid || '',
      options: { ...registrationOptions, ...participantData?.options },
      goal: participantData?.goal ?? '',
      file: null,
      image: participantData?.image ?? undefined,
    },
    validate: {
      first_name:
        hasLength({ min: 2 }, 'First name must be more than 1 character long') ||
        isNotEmpty('First name is required'),
      last_name:
        hasLength({ min: 2 }, 'Last name must be more than 1 character long') ||
        isNotEmpty('Last name is required'),
      email: isEmail('Invalid email address'),
    },
  });

  useFormListValidator({ form, id: 'participant' });

  return (
    <Stack gap="lg" key={participantData?.uuid} mb="lg">
      <Group grow>
        <TextInput
          label="First Name"
          h="100%"
          maw={'calc(50% - 12px)'}
          placeholder="Enter first name"
          required
          {...form.getInputProps('first_name')}
        />
        <TextInput
          label="Last Name"
          h="100%"
          maw="calc(50% - 12px)"
          placeholder="Enter last name"
          required
          {...form.getInputProps('last_name')}
        />
      </Group>
      <Group>
        <TextInput
          label="Email Address"
          h="100%"
          maw={'calc(50% - 12px)'}
          style={{ flexGrow: 1 }}
          placeholder="Enter email address"
          required
          {...form.getInputProps('email')}
        />
        {campaignSettings.isTeamsEnabled && !!teamsData?.length && (
          <Select
            label={`Select ${t('team')}`}
            h="100%"
            style={{ flexGrow: 1 }}
            data={teams || []}
            placeholder={`Select ${t('team').toLowerCase()}`}
            {...form.getInputProps('team_id')}
          />
        )}
      </Group>
      {isSetGoal &&
        (campaign?.participant_options.goal_type === 'currency' ? (
          <CurrencyInput label="Goal" maw={'calc(50% - 12px)'} {...form.getInputProps('goal')} />
        ) : (
          <NumberInput
            hideControls
            allowLeadingZeros={false}
            allowNegative={false}
            label={`${unit} Goal`}
            placeholder={`Set ${unit} goal`}
            maw={'calc(50% - 12px)'}
            {...form.getInputProps('goal')}
            value={Number(form?.values.goal)}
          />
        ))}
      <Stack gap={4}>
        <ImageUpload
          label={`Upload a ${t('participant')} Image`}
          dimensions={[600, 300]}
          file={form.values.file}
          setFile={(file) => {
            form.setFieldValue('file', file);
          }}
          existingSrc={form.values.image}
          setExistingSrc={() => form.setFieldValue('image', undefined)}
          enableCropping
        />
        {form.errors.file && <Input.Error>{form.errors.file}</Input.Error>}
      </Stack>
      <AdditionalFieldsFormsGuard
        options={registrationFields}
        onChange={(_value, formattedValues) =>
          form.setFieldValue('options', formattedValues.asObject)
        }
        values={form.values.options}
      />
    </Stack>
  );
};

export const AddEditPeerContent = ({
  peer_uuid,
  peer_type,
}: {
  peer_uuid?: string;
  peer_type: PeerTypes;
}) => {
  const { campaignId, causeId } = useCrowdfundingProvider();

  const queryOptions = useMemo(
    () => (peer_type === 'participants' ? { options: { include: 'last_name' } } : {}),
    [peer_type]
  );

  const { isInitialLoading, data } = useShowCampaignPeer({
    participantId: peer_uuid,
    peerType: peer_type,
    campaignId,
    causeId,
    queryOptions,
  });

  return (
    <Stack gap="lg" key={peer_uuid}>
      <LoadingOverlay visible={isInitialLoading} />
      {!isInitialLoading &&
        (peer_type === 'teams' ? (
          <AddEditTeamForm teamData={data as PeerTeamWith__Type} />
        ) : (
          <AddEditParticipantForm participantData={data as PeerParticipantsWith__Type} />
        ))}
    </Stack>
  );
};
